import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryPlacementRegistryQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { isPartsContent, toDraft, toPartsContent } from "@/lib/assetContent";
import {
  BG_CONVERSATION_PROMPT_ROLE,
  BG_PERSONALITY_ROLE,
  BG_STRUCTURE_LANGUAGE,
  BG_TONE_ROLE,
  blankToneDocument,
  storedProfileSummary,
} from "@/lib/backgroundProfile";
import { libraryAssetHref } from "@/lib/librarySections";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { LoadingState } from "@/ui/states";

import { NamedReadFailureState } from "./NamedReadFailure";

/**
 * Create a shared BG interview profile, or an editable copy of one (#914).
 *
 * ## WHY THIS FAMILY GETS ITS OWN DIALOG
 *
 * `NewAssetDialog` authors a v1 through `PartsEditor` — a free-text "Part key"
 * input over a raw JSON textarea — which the 2026-08-25 amendment forbids and
 * which the ratified contract lists among the rejected mechanisms. So
 * `background_profile` was simply absent from `CREATABLE_ASSET_TYPES` when #858
 * landed the family, and #859's catalog says so in as many words: a create
 * control whose only destination authors free parts over a typed tone document
 * "would be the dead affordance ADR 0001 forbids". This dialog is the
 * destination that removes both problems.
 *
 * ## THE TWO PROMPTS ARE ASKED FOR, AND WHY THAT IS NOT OPTIONAL
 *
 * `NewBackgroundDomainDialog` and `NewPathwayDialog` both require an existing
 * asset as exact starting content, on the rule that a created v1 is immediately
 * adoptable so a placeholder document would make placeholder content clinical
 * content. That rule cannot be met the same way here: base canon ships NO
 * variant at all — "the seed importer never assembles one" — so the FIRST
 * profile in any library has nothing to copy, and a dialog that demanded a
 * source would be a create control that can never be used.
 *
 * So this dialog asks for the content instead. An earlier form published a v1
 * whose two prompts carried a "this has not been written yet" sentinel, and
 * that was wrong for this family in a way it is not for a main concern: the
 * service's completeness check is role PRESENCE, so such a profile is adoptable,
 * validates, freezes and exports — and the pinned engine then reads those two
 * sentences as the personality and the conversation prompt of every background
 * turn. S7 contract §4 rules it out in as many words: "Missing profile text
 * cannot be replaced with Lovable's placeholder."
 *
 * THE TONE DOCUMENT IS STILL CREATED EMPTY, and that is a different thing
 * rather than the same compromise. Every value `blankToneDocument` writes is a
 * posture the engine actually honours — `bg_intro: ""` is "no preamble", an
 * empty transition clause is "suppress this bridge", `session_intro.enabled:
 * false` is MOD's own opening. None of them is prose fed to a model. A blank
 * prompt is refused by `min_length=1` precisely because there is no such
 * reading of it.
 *
 * ## THE COORDINATE IS THE VARIANT, AND IT IS ONE SEGMENT
 *
 * A profile's placement coordinate is the variant name and nothing else — the
 * value that becomes `flow_config.bg_variant`, and the directory its three
 * fixed-name files land in. The SHAPE it must take is read from the placement
 * registry (`families[].placement.segmentPattern`) and never restated here, for
 * `NewBackgroundDomainDialog`'s reason: the service publishes the pattern
 * precisely so a client can validate against the same rule that refuses.
 *
 * ## LANGUAGES ARE CHOSEN HERE BECAUSE THEY CANNOT BE CHOSEN LATER
 *
 * `filesystem_provider._validate_bg_variant` demands a complete variant for
 * every language a project serves, and a profile short of one is refused at
 * adoption with `placement.background_profile_conflict`. Both source packs'
 * profiles are Hebrew-only, and a project narrows `supported_languages` to
 * match (#854); a project serving both needs a profile carrying both. There is
 * no control that adds a language to an existing profile, so asking once, here,
 * is what keeps that outcome reachable without a raw parts editor — and the
 * prompts are asked for PER LANGUAGE, because a profile whose English half is
 * Hebrew text is complete as far as the service can tell and wrong as far as
 * the runtime is concerned.
 */

const NAME_MAX_LENGTH = 60;

/** How many segments a background-profile coordinate carries, per #858. */
const EXPECTED_SEGMENTS = 1;

/** The second language a profile may carry, beside the structure anchor. */
const SECONDARY_LANGUAGE = "en";

const REFUSED_CONTENT =
  "This profile's content could not be prepared, so nothing was created.";

/** One language's authored prompts. */
interface LanguagePrompts {
  conversationPrompt: string;
  personality: string;
}

/**
 * The v1 parts for a profile authored here: the empty tone document, and the
 * prompts the clinician wrote FOR THAT LANGUAGE.
 *
 * EACH LANGUAGE IS AUTHORED SEPARATELY, and that is the #914 review's
 * correction. An earlier form wrote one pair of values under both `he/*` and
 * `en/*`: the service checks that the roles EXIST, not that English content is
 * English, so the v1 was immediately adoptable and exportable while one runtime
 * language served text written for the other. There is no honest single-field
 * shortcut here — a prompt is prose a model is handed verbatim — so the dialog
 * asks per language and refuses a blank one.
 */
function authoredParts(prompts: ReadonlyMap<string, LanguagePrompts>) {
  return [...prompts].flatMap(([language, written]) => [
    {
      kind: "structured" as const,
      key: `${language}/${BG_TONE_ROLE}`,
      data: JSON.parse(blankToneDocument()) as Record<string, unknown>,
    },
    {
      kind: "prompt_text" as const,
      key: `${language}/${BG_PERSONALITY_ROLE}`,
      text: written.personality,
    },
    {
      kind: "prompt_text" as const,
      key: `${language}/${BG_CONVERSATION_PROMPT_ROLE}`,
      text: written.conversationPrompt,
    },
  ]);
}

export function NewBackgroundProfileDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens as "create an editable copy of this profile". */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const registry = useLibraryPlacementRegistryQuery();
  const nameErrorId = useId();
  const variantErrorId = useId();
  const placementErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [variant, setVariant] = useState("");
  const [bilingual, setBilingual] = useState(false);
  // ONE PAIR PER LANGUAGE, keyed by the language it will be stored at. A pair
  // the clinician never sees (English, while "Hebrew only" is selected) is
  // never read, so switching back and forth loses nothing typed.
  const [prompts, setPrompts] = useState<Record<string, LanguagePrompts>>({});
  const [blocked, setBlocked] = useState<string | null>(null);
  // THE SAME GATE THE CATALOG'S CONTROLS CARRY. A reader reaching this dialog —
  // through a copy control, a stale render, a direct mount — must not be able
  // to submit a create the service will refuse; ADR 0001's rule is that a
  // control says why it cannot be used rather than failing at the server.
  const write = useCapability("library.write");

  const source = useAssetVersionQuery(
    copyFrom?.id ?? "",
    copyFrom ? copyFrom.latestVersion : null,
  );
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom?.id ?? "", copyFrom?.latestVersion ?? ""),
  );
  const retryRegistry = useRetryPausedQuery(queryKeys.libraryPlacementRegistry);

  const placement = registry.data?.families.find(
    (family) => family.assetType === "background_profile",
  )?.placement;
  // THE SERVICE'S OWN GRAMMAR, anchored in this dialect and nowhere restated.
  // The registry publishes the pattern UNANCHORED precisely so each client
  // anchors it in its own regex syntax.
  const segmentPattern = placement?.segmentPattern ?? null;
  const segmentRule =
    segmentPattern === null ? null : new RegExp(`^(?:${segmentPattern})$`);
  // A registry that ANSWERED and no longer describes a one-segment coordinate
  // for this family: the shape changed under this build, and a coordinate this
  // dialog assembled would be one the service never asked for.
  const policyUnusable =
    registry.isSuccess &&
    (placement?.kind !== "coordinate" ||
      placement.coordinateSegments !== EXPECTED_SEGMENTS);

  // AN UNREAD POLICY IS NOT A PERMISSIVE ONE — `NewBackgroundDomainDialog`'s
  // ruling, and #859's first review finding. `segmentRule` is null while the
  // registry is pending or failed, and a null rule makes every segment valid, so
  // without the registry among this dialog's prerequisites a policy read that
  // never arrived would validate nothing and submit against a grammar it had not
  // read. `policyUnusable` cannot cover it: it is deliberately scoped to a
  // SUCCESSFUL read, because "the shape is wrong" and "we do not know the shape"
  // are different facts with different remedies.
  const variantValid = segmentRule === null || segmentRule.test(variant);

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;

  /** The languages this create would author, in the order they are stored. */
  const languages = bilingual
    ? [BG_STRUCTURE_LANGUAGE, SECONDARY_LANGUAGE]
    : [BG_STRUCTURE_LANGUAGE];
  const promptsFor = (language: string): LanguagePrompts =>
    prompts[language] ?? { conversationPrompt: "", personality: "" };
  const promptsComplete = languages.every((language) => {
    const written = promptsFor(language);
    return (
      written.conversationPrompt.trim() !== "" &&
      written.personality.trim() !== ""
    );
  });
  function setPrompt(
    language: string,
    field: keyof LanguagePrompts,
    value: string,
  ) {
    setPrompts((current) => ({
      ...current,
      [language]: { ...promptsFor(language), [field]: value },
    }));
  }

  function submit() {
    setBlocked(null);
    if (write.reason !== null) {
      return;
    }

    let parts;
    if (copyFrom) {
      const stored = source.isSuccess ? source.data.content : null;
      if (stored === null || !isPartsContent(stored)) {
        setBlocked(
          "The Editor Service did not return usable starting content for this profile, so nothing was created.",
        );
        return;
      }
      // NOTHING IS REWRITTEN in a copied profile, which is the difference from
      // the domain dialog. A domain document names its own `domain` and the
      // service checks it against the coordinate's slug; a tone document names
      // no variant at all — the coordinate is the only place the variant is
      // stated, and the two file references inside it are generated at emission
      // from that coordinate. So every stored byte carries through as it is.
      const content = toPartsContent(toDraft(stored));
      if (!content.ok) {
        setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
        return;
      }
      parts = content.content;
    } else {
      parts = {
        kind: "parts" as const,
        parts: authoredParts(
          new Map(languages.map((language) => [language, promptsFor(language)])),
        ),
      };
    }

    create.mutate(
      {
        assetType: "background_profile",
        name: name.trim(),
        content: parts,
        placementCoordinate: variant.trim(),
        ...(copyFrom
          ? {
              note: `Started from "${copyFrom.name}" at ${copyFrom.latestVersion}.`,
            }
          : {}),
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(libraryAssetHref(created.assetType, created.id));
        },
      },
    );
  }

  const sourceFailure =
    copyFrom !== undefined && (source.isPaused || source.isError)
      ? {
          failure: source.isPaused ? source.failureReason : source.error,
          isPaused: source.isPaused,
          retry: retrySource,
        }
      : null;
  const registryFailure =
    registry.isPaused || registry.isError
      ? {
          failure: registry.isPaused ? registry.failureReason : registry.error,
          isPaused: registry.isPaused,
          retry: retryRegistry,
        }
      : null;
  // Paused is tested first inside each arm: a held retry keeps `isPending` true
  // with `isError` false, so testing pending first would put a permanent
  // skeleton over a failure the Editor Service already sent.
  const readFailure = sourceFailure ?? registryFailure;
  const reading =
    readFailure === null &&
    (registry.isPending || (copyFrom !== undefined && source.isPending));

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : "New interview profile"}
        description={
          copyFrom
            ? "Creates your own interview profile at another variant, starting from this one's tone and prompts. The original stays exactly as it is."
            : "A shared interview profile: the tone, opening and prompts every background area of one variant is read under. Its prompts are written here, in each language it will serve; its tone and openings are authored afterwards on the profile's own page."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                reading ||
                readFailure !== null ||
                write.reason !== null ||
                // STATED LOCALLY as well as composed above: nothing is created
                // without a served placement policy, and this is the invariant
                // that must survive somebody rearranging that composition.
                !registry.isSuccess ||
                policyUnusable ||
                name.trim() === "" ||
                variant.trim() === "" ||
                !variantValid ||
                // THE PROMPTS ARE REQUIRED of a profile authored here, at EVERY
                // language it will carry — not of a copy, which brings the
                // source's own. Blank is refused by `PromptPart.text`'s
                // `min_length=1` anyway; refusing it here is what stops a v1
                // that only LOOKS authored, and asking per language is what
                // stops one language serving the other's text.
                (!copyFrom && !promptsComplete)
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : "Create profile"}
            </Button>
          </>
        }
      >
        {readFailure ? (
          <NamedReadFailureState
            failure={readFailure.failure}
            isPaused={readFailure.isPaused}
            retry={readFailure.retry}
            subject="Library"
            title="This interview profile could not be prepared"
            description="The Editor Service did not return the placement policy this dialog needs, so nothing can be created yet."
            deniedDescription="You are not authorized to read the shared clinical library."
            offlineDescription="The shared clinical library could not be reached. It loads on its own when the connection returns."
          />
        ) : reading ? (
          <LoadingState label="Loading placement policy" rows={3} />
        ) : (
          <div className="space-y-4">
            {write.reason ? (
              <Notice role="alert">{write.reason}</Notice>
            ) : null}

            {policyUnusable ? (
              <Notice role="alert" tone="destructive">
                The Editor Service does not describe an interview profile&apos;s
                pack placement in a shape this version can assemble, so no
                profile can be created here. Nothing has been changed.
              </Notice>
            ) : null}

            <Field
              label="Name"
              description="What this profile is called in the Clinical library. Its tone and prompts are written afterwards on the profile's own page."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={name}
                    maxLength={NAME_MAX_LENGTH}
                    placeholder="e.g. MOD interview profile"
                    aria-invalid={fieldErrors.name !== undefined || undefined}
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.name ? nameErrorId : undefined,
                    )}
                    onChange={(event) => setName(event.target.value)}
                  />
                  {fieldErrors.name ? (
                    <FieldError id={nameErrorId} message={fieldErrors.name} />
                  ) : null}
                </>
              )}
            </Field>

            <Field
              label="Profile variant"
              description="The runtime identity of this interview flavour. A project selects one profile by this name, and every background domain it pins has to belong to the same one. It is immutable once created."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Input
                    id={inputId}
                    value={variant}
                    dir="ltr"
                    placeholder="e.g. mod"
                    aria-invalid={(!variantValid && variant !== "") || undefined}
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      !variantValid && variant !== ""
                        ? variantErrorId
                        : undefined,
                    )}
                    onChange={(event) => setVariant(event.target.value)}
                  />
                  {!variantValid && variant !== "" ? (
                    <FieldError
                      id={variantErrorId}
                      message="The Editor Service does not accept this as a pack placement segment."
                    />
                  ) : null}
                </>
              )}
            </Field>

            {fieldErrors.placementCoordinate ? (
              <FieldError
                id={placementErrorId}
                message={fieldErrors.placementCoordinate}
              />
            ) : null}

            {copyFrom
              ? null
              : languages.map((language) => (
                  /* THE CONTENT, asked for rather than stubbed, AND PER
                     LANGUAGE. The pinned engine reads both of these on every
                     background turn of this variant, and the service's
                     completeness check is role presence — so neither a "not
                     written yet" sentence nor Hebrew stored under `en/` would
                     be caught before it was adopted, exported and served. */
                  <div key={language} className="space-y-4">
                    {languages.length > 1 ? (
                      <h3 className="text-sm font-medium text-foreground">
                        {language === BG_STRUCTURE_LANGUAGE
                          ? "Hebrew"
                          : "English"}
                      </h3>
                    ) : null}

                    <Field
                      label={
                        languages.length > 1
                          ? `Conversation prompt (${language})`
                          : "Conversation prompt"
                      }
                      description="Read on every background turn of this profile: how the interview talks, what it may and may not do. It is edited afterwards on the profile's own page."
                    >
                      {({ inputId, describedBy }) => (
                        <Textarea
                          id={inputId}
                          aria-describedby={describedBy}
                          rows={6}
                          dir="auto"
                          value={promptsFor(language).conversationPrompt}
                          onChange={(event) =>
                            setPrompt(
                              language,
                              "conversationPrompt",
                              event.target.value,
                            )
                          }
                        />
                      )}
                    </Field>

                    <Field
                      label={
                        languages.length > 1
                          ? `Personality (${language})`
                          : "Personality"
                      }
                      description="Who the interview presents itself as. Read alongside the conversation prompt on every background turn."
                    >
                      {({ inputId, describedBy }) => (
                        <Textarea
                          id={inputId}
                          aria-describedby={describedBy}
                          rows={5}
                          dir="auto"
                          value={promptsFor(language).personality}
                          onChange={(event) =>
                            setPrompt(
                              language,
                              "personality",
                              event.target.value,
                            )
                          }
                        />
                      )}
                    </Field>
                  </div>
                ))}

            {copyFrom ? (
              <SourcePreview source={source} />
            ) : (
              <Field
                label="Languages"
                description="Which languages this profile carries a complete tone and prompt set at. A project can only adopt a profile that covers every language it serves, and there is no way to add one afterwards."
              >
                {({ inputId, describedBy }) => (
                  <Select
                    id={inputId}
                    aria-describedby={describedBy}
                    value={bilingual ? "both" : "he"}
                    onChange={(event) =>
                      setBilingual(event.currentTarget.value === "both")
                    }
                  >
                    <option value="he">
                      Hebrew only — for a project that serves Hebrew
                    </option>
                    <option value="both">
                      Hebrew and English — for a project that serves both
                    </option>
                  </Select>
                )}
              </Field>
            )}

            {generalMessage ? (
              <Notice role="alert" tone="destructive">
                {generalMessage}
              </Notice>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the copied version actually holds, before it becomes v1.
 *
 * FACTS ABOUT COVERAGE, never the clinical text: a clinician copying a profile
 * needs to know which languages come with it, because that is what decides
 * whether the copy can be adopted by their project at all.
 */
function SourcePreview({
  source,
}: {
  source: { isSuccess: boolean; data?: { content: unknown } | undefined };
}) {
  if (!source.isSuccess || source.data === undefined) {
    return null;
  }
  const summary = storedProfileSummary(source.data.content);
  if (summary === null || summary.completeLanguages.length === 0) {
    return null;
  }
  return (
    <p className="text-xs text-muted-foreground">
      Copying a complete tone and prompt set in{" "}
      {summary.completeLanguages.join(", ")}
      {summary.bannedPhraseCount === null
        ? ""
        : `, with ${summary.bannedPhraseCount} recorded ${
            summary.bannedPhraseCount === 1 ? "phrase" : "phrases"
          } to avoid`}
      .
    </p>
  );
}
